<script setup lang="ts">
/**
 * The headline figures.
 *
 * Each figure is a small card rather than a run of loose fields, so it is
 * obvious which label belongs to which value once there are five of them.
 *
 * Direction is a choice of three, not a calculation. Whether a fall is good
 * news depends on what fell, and an interface that decides for itself will
 * colour a reduction in costs red.
 */
import { useI18n } from 'vue-i18n'

import InspectorField from '@/sections/InspectorField.vue'
import { newKpi, type Kpi, type KpiSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: KpiSection }>()
const emit = defineEmits<{ change: [section: KpiSection] }>()

const DIRECTIONS = [
  { value: 'auto', key: 'neutral' },
  { value: 'up', key: 'good' },
  { value: 'down', key: 'poor' },
] as const

function set(changes: Partial<KpiSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setItem(id: string, changes: Partial<Kpi>): void {
  set({
    items: props.section.items.map((item) => (item.id === id ? { ...item, ...changes } : item)),
  })
}

function add(): void {
  set({ items: [...props.section.items, newKpi()] })
}

function remove(id: string): void {
  set({ items: props.section.items.filter((item) => item.id !== id) })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.kpi.title')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <div class="items">
      <article v-for="item in section.items" :key="item.id" class="item">
        <header class="item__head">
          <input
            class="b-input item__label"
            :value="item.label"
            :placeholder="t('inspector.kpi.labelPlaceholder')"
            @input="setItem(item.id, { label: ($event.target as HTMLInputElement).value })"
          />
          <button
            type="button"
            class="b-icon-btn"
            :aria-label="t('inspector.kpi.removeFigure')"
            @click="remove(item.id)"
          >
            &times;
          </button>
        </header>

        <div class="item__row">
          <input
            class="b-input"
            :value="item.value"
            :placeholder="t('inspector.kpi.valuePlaceholder')"
            @input="setItem(item.id, { value: ($event.target as HTMLInputElement).value })"
          />
          <input
            class="b-input"
            :value="item.change"
            :placeholder="t('inspector.kpi.changePlaceholder')"
            @input="setItem(item.id, { change: ($event.target as HTMLInputElement).value })"
          />
        </div>

        <div class="item__tones" role="group" :aria-label="t('inspector.kpi.toneGroup')">
          <button
            v-for="option in DIRECTIONS"
            :key="option.value"
            type="button"
            class="tone"
            :class="[`tone--${option.value}`, { 'tone--on': item.direction === option.value }]"
            :aria-pressed="item.direction === option.value"
            @click="setItem(item.id, { direction: option.value })"
          >
            {{ t(`inspector.kpi.${option.key}`) }}
          </button>
        </div>
      </article>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.kpi.addFigure') }}
    </button>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.items {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.item {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-5);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.item__head {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
}

.item__label {
  font-weight: 500;
}

.item__row {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: var(--sp-3);
}

.item__tones {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-2);
}

.tone {
  height: 24px;
  border: 1px solid var(--hairline);
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-faint);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
  transition:
    border-color var(--quick),
    color var(--quick);
}

.tone:hover {
  color: var(--ink);
}

.tone--on.tone--auto {
  border-color: var(--hairline-strong);
  color: var(--ink);
}

.tone--on.tone--up {
  border-color: color-mix(in srgb, var(--good) 55%, transparent);
  color: var(--good);
}

.tone--on.tone--down {
  border-color: color-mix(in srgb, var(--danger) 55%, transparent);
  color: var(--danger);
}
</style>
